import { Divider, Stack, Typography } from '@mui/material';
import { useCallback } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { LinkBroken01Icon } from '@/components/icons/LinkBroken01Icon';
import { Card } from '@/components/layout/Card/Card';
import { useFormContext } from '@/components/react-hook-form';
import { useModalState } from '@/hooks/useModalState';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { DispositiveProvisionsFormRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { useDispositiveProvisionTemplateDisplayData } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/hooks/useDispositiveProvisionTemplateDisplayData';
import {
  DispositiveProvisionsFormV2Shape,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { getDeathEventBasePath } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.utils';
import { DispositiveProvisionsTemplateSplitScreenModal } from '@/modules/dispositiveProvisions/DispositiveProvisionsTemplateSplitScreenModal/DispositiveProvisionsTemplateSplitScreenModal';
import { COLORS } from '@/styles/tokens/colors';
import { DispositiveProvisionTemplateKind } from '@/types/schema';
import { PathsOf } from '@/types/subform';

import { PrimaryClientDeathSectionTemplateRecipient } from './PrimaryClientDeathSectionTemplateRecipient';

interface PrimaryClientDeathSectionTemplateRecipientsProps {
  deathEvent: PrimaryClientDeathEvent;
  templateId: string;
}

export function PrimaryClientDeathSectionTemplateRecipients({
  deathEvent,
  templateId,
}: PrimaryClientDeathSectionTemplateRecipientsProps) {
  const { setValue } = useFormContext<DispositiveProvisionsFormV2Shape>();
  const canEditHousehold = useCanEditDispositiveProvisions();
  const { isFromTemplateEditor } = useDispositiveProvisionsContext();
  const {
    displayName,
    recipientsAsFormData: recipientsFromTemplate,
    template,
  } = useDispositiveProvisionTemplateDisplayData(templateId);

  const [
    { isModalOpen: isEditTemplateModalOpen },
    { openModal: openEditTemplateModal, closeModal: closeEditTemplateModal },
  ] = useModalState();

  const recipientsPath = `${getDeathEventBasePath(
    deathEvent
  )}.recipients` as const satisfies PathsOf<DispositiveProvisionsFormV2Shape>;

  const templateIdPath = `${getDeathEventBasePath(
    deathEvent
  )}.templateId` as const satisfies PathsOf<DispositiveProvisionsFormV2Shape>;

  const handleUnlinkTemplate = useCallback(() => {
    setValue(templateIdPath, null, { shouldDirty: true });
    if (template?.kind === DispositiveProvisionTemplateKind.PouroverWill) {
      setValue(recipientsPath, [], { shouldDirty: true });
    }
  }, [setValue, template?.kind, templateIdPath, recipientsPath]);

  const handleEditTemplate = useCallback(() => {
    openEditTemplateModal();
  }, [openEditTemplateModal]);

  const onCloseEditTemplateModal = useCallback(
    (
      updatedTemplateId?: string,
      updatedRecipients?: DispositiveProvisionsFormRecipient[],
      isDeleted?: boolean
    ) => {
      if (isDeleted) {
        setValue(templateIdPath, null, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        setValue(recipientsPath, [], {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
        closeEditTemplateModal();
        return;
      }

      if (!updatedTemplateId || !updatedRecipients?.length) {
        closeEditTemplateModal();
        return;
      }

      setValue(templateIdPath, updatedTemplateId, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      setValue(recipientsPath, updatedRecipients, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      closeEditTemplateModal();
    },
    [closeEditTemplateModal, recipientsPath, setValue, templateIdPath]
  );

  return (
    <>
      {isEditTemplateModalOpen && (
        <DispositiveProvisionsTemplateSplitScreenModal
          isOpen={isEditTemplateModalOpen}
          onClose={onCloseEditTemplateModal}
          templateDetails={{ templateId }}
          isFromEditingDispositions
        />
      )}
      <Card variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <Stack spacing={0.25}>
            <Typography variant="title-s">{displayName}</Typography>
            {template?.proRata != null && (
              <Typography variant="body-xs">
                {template.proRata
                  ? 'Disposition amounts will be sourced from the total sum of all entities linked to this template.'
                  : 'Disposition amounts will be sourced from each entity linked to this template.'}
              </Typography>
            )}
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            flexShrink={0}
            alignItems="flex-start"
          >
            {!isFromTemplateEditor && (
              <Button
                variant="secondary"
                size="xs"
                onClick={handleEditTemplate}
              >
                {canEditHousehold ? 'Edit template' : 'View template'}
              </Button>
            )}
            <Button
              variant="secondary"
              startIcon={LinkBroken01Icon}
              size="xs"
              onClick={handleUnlinkTemplate}
              disabled={!canEditHousehold}
            >
              Unlink
            </Button>
          </Stack>
        </Stack>
        <Stack
          divider={<Divider sx={{ borderColor: COLORS.GRAY[200] }} />}
          sx={{
            px: 2,
            mt: 3,
            backgroundColor: COLORS.GRAY['50'],
            borderRadius: '4px',
            border: `1px solid ${COLORS.GRAY['200']}`,
          }}
        >
          {recipientsFromTemplate?.map((recipient) => (
            <PrimaryClientDeathSectionTemplateRecipient
              key={recipient.recipientId}
              recipient={recipient}
            />
          ))}
        </Stack>
      </Card>
    </>
  );
}
